import React, { FunctionComponent, useEffect, useState } from 'react';

import { flatten, includes, isEmpty, map, uniqBy } from 'lodash';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { OptionTypeBase, ValueType } from 'react-select';

import { sendInternalEmail } from 'admin/my-account/MyAccount';
import { FieldName as UserFieldName, getUsers } from 'admin/users/Users';
import { useProfile } from 'auth/Auth';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { AssetUploader } from 'common/upload/asset-uploader/AssetUploader';
import { UploadedFileAsset } from 'common/upload/Upload';
import { OnFilesAcceptedFunc } from 'common/upload/uploader/Uploader';
import { Overlay } from 'core/overlay/Overlay';
import { Select } from 'core/select/Select';

import 'common/chrome/top-nav/internal-emailer/InternalEmailer.scss';

interface Props {
    show: boolean;
    onClose: () => void;
}

enum FormControl {
    Recipients = 'recipients',
    Subject = 'subject',
    Body = 'body',
    Files = 'files',
}

interface FormInputs {
    // [FormControl.Recipients]: string[];
    [FormControl.Subject]: string;
    [FormControl.Body]: string;
    [FormControl.Files]?: string[];
}

export const InternalEmailer: FunctionComponent<Props> = (props) => {
    const { onClose, show } = props;
    const { t } = useTranslation('common/chrome');
    const [{ profile }] = useProfile();
    const [, dispatchMessage] = useMessage();

    const [recipientOptions, setRecipientOptions] = useState<OptionTypeBase[]>([]);
    const [selectedRecipients, setSelectedRecipients] = useState<ValueType<OptionTypeBase>>();
    const [attachments, setAttachments] = useState<UploadedFileAsset[]>([]);
    const { formState, handleSubmit, register, reset } = useForm<FormInputs>();
    const { errors } = formState;
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        if (show && !recipientOptions.length) {
            getRecipientOptions();
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const getRecipientOptions = (): void => {
        const search = {
            filters: [
                {
                    name: UserFieldName.Active,
                    values: ['true'],
                },
            ],
            // TODO projections
        };
        const args = {
            basic: false,
            includeRoles: true,
        };

        (async (): Promise<any> => {
            try {
                const users = await trackPromise(
                    getUsers(profile!.user.agencyID, profile!.user.tenantID, search, args),
                    'recipients',
                );

                const roles = uniqBy(flatten(map(users, 'roles')), 'id');
                const options: OptionTypeBase[] = [];
                let roleUsers;

                roles.forEach((role) => {
                    if (role.id) {
                        roleUsers = users.filter((user) => includes(user.roleIDs, role.id));

                        options.push({
                            value: role.id,
                            label: `${role.name} (${roleUsers.length} ${
                                roleUsers.length === 1 ? t('USER') : t('USERS')
                            })`,
                        });
                    }
                });

                setRecipientOptions(options);
            } catch {}
        })();
    };

    const onFilesAccepted: OnFilesAcceptedFunc = (currentFiles, allFiles) => {
        const files = allFiles as UploadedFileAsset[];

        setAttachments(files);
    };

    const onSubmit = (data: FormInputs): void => {
        (async (): Promise<any> => {
            try {
                const args = {
                    subject: data[FormControl.Subject],
                    body: data[FormControl.Body],
                    roleIds: (isEmpty(selectedRecipients) ? recipientOptions : selectedRecipients)!.map(
                        (recipient: OptionTypeBase) => recipient.value,
                    ),
                };

                if (!isEmpty(attachments)) {
                    args.body += '<br/>';
                    attachments.forEach((attachment) => {
                        args.body += `<br/><a href="${attachment.url}" target="_blank">${attachment.name}</a>`;
                    });
                }

                await trackPromise(sendInternalEmail(profile!.user.tenantID, profile!.user.id, args), 'modal');

                close();

                dispatchMessage({
                    type: MessageActionType.ShowSuccess,
                    text: t('EMAIL_SENT'),
                });
            } catch {
                setShowError(true);
            }
        })();
    };

    const close = (): void => {
        setSelectedRecipients([]);
        setAttachments([]);
        setShowError(false);

        dispatchMessage({ type: MessageActionType.Hide });
        reset();

        onClose();
    };

    /*
     * TODO:
     *  - nameOnly option for AssetUploader
     */

    return (
        <Modal size="lg" show={show} onHide={close} className="r4e-internal-emailer">
            <Overlay area="modal" />
            <Modal.Header closeButton>
                <Modal.Title>{t('INTERNAL_EMAILER')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {showError ? (
                    <Alert variant="danger" dismissible onClose={(): void => setShowError(false)}>
                        <i className="icon-exclamation-circle" />
                        <span>{t('FAILED_TO_SEND_EMAIL')}</span>
                    </Alert>
                ) : (
                    ''
                )}
                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <Form.Group as={Row} controlId={FormControl.Recipients}>
                        <Form.Label column sm="3">
                            {t('RECIPIENTS')}
                        </Form.Label>
                        <Overlay area="recipients" />
                        <Col sm="9">
                            <Select
                                name={FormControl.Recipients}
                                isMulti
                                options={recipientOptions}
                                value={selectedRecipients}
                                onChange={setSelectedRecipients}
                            />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId={FormControl.Subject}>
                        <Form.Label column sm="3">
                            {t('SUBJECT')}
                        </Form.Label>
                        <Col sm="9">
                            <Form.Control
                                ref={register({ required: true })}
                                name={FormControl.Subject}
                                type="text"
                                isInvalid={!!errors?.[FormControl.Subject]}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('VALIDATION_SUBJECT_REQUIRED')}
                            </Form.Control.Feedback>
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId={FormControl.Body}>
                        <Form.Label column sm="3">
                            {t('MESSAGE')}
                        </Form.Label>
                        <Col sm="9">
                            <Form.Control
                                ref={register({ required: true })}
                                name={FormControl.Body}
                                as="textarea"
                                rows={3}
                                isInvalid={!!errors?.[FormControl.Body]}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('VALIDATION_BODY_REQUIRED')}
                            </Form.Control.Feedback>
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId={FormControl.Files}>
                        <Form.Label column sm="3">
                            {t('ATTACHMENTS')}
                        </Form.Label>
                        <Col sm="9">
                            <AssetUploader
                                agencyId={profile!.user.agencyID}
                                tenantId={profile!.user.tenantID}
                                media={attachments}
                                // displayGallery={false}
                                uploaderOptions={{
                                    accept: `${[
                                        'audio/*',
                                        'image/*',
                                        'text/*',
                                        'video/*',
                                        'application/pdf',
                                        'application/msword',
                                        'application/vnd.ms-excel',
                                        'application/vnd.ms-powerpoint',
                                        'application/vnd.ms-word',
                                    ].join(',')}`,
                                    maxFiles: 10,
                                    multiple: true,
                                    uploadToCloud: true,
                                    onFilesAccepted,
                                }}
                            />
                            <Form.Text className="text-muted">{t('WARNING_SENSITIVE_ATTACHMENTS')}</Form.Text>
                        </Col>
                    </Form.Group>
                    <Row>
                        <Col className="text-right">
                            <Button variant="secondary" type="button" className="mr-2" onClick={close}>
                                {t('CANCEL')}
                            </Button>
                            <Button variant="primary" type="submit">
                                {t('SEND_EMAIL')}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
        </Modal>
    );
};
